import { useEffect } from 'react';

const MEDIA_CONTENT_CHANGED = 'media-content-changed';

/**
 * Run a callback when the media content of this project has changed.
 *
 * @param {function} callback - A function that runs when new medias have been added to the project.
 *
 * Parts of the Project page that are not directly under the direct control of
 * uploading, moving, or copying may know when media have been added. It's the
 * responsibility of those parts to call triggerOnMediaContentChanged().
 */
export const useOnMediaContentChanged = (callback) => {
  useEffect(() => {
    const listener = window.addEventListener(
      MEDIA_CONTENT_CHANGED,
      () => {
        // This is intentionally passed inside an anonymous function to enforce
        // that the callback doesn't have access to any details about the event.
        // The idea is that it won't necessarily know _which_ new medias have been
        // change, just that some have been added or removed or mutated.
        callback();
      },
      false,
    );
    return () => {
      window.removeEventListener(MEDIA_CONTENT_CHANGED, listener, false);
    };
  }, [callback]);
};

/**
 * Trigger the "media-content-changed" event. Anywhere we are using
 * useOnMediaContentChanged(), it will run the callback.
 */
export const triggerOnMediaContentChanged = () => {
  const event = new Event(MEDIA_CONTENT_CHANGED);
  window.dispatchEvent(event);
};
